import { AxiosResponse } from 'axios';
import qs from 'qs';

import {
  Guid,
  InvoiceDetailDto,
  InvoiceFilterOptions,
  Invoices,
  PaginatedList,
  ReportFilterProps,
  ServiceResult,
} from '@project/types';
import { authNetworkService } from '@project/services';
import { ContentTypeEnum } from '@project/enums';

import adminInvoicesUrls from './admin-invoices.urls';
import { CorrespondenceType } from '../user-auth/types';

export const getInvoices = (filter: InvoiceFilterOptions): Promise<PaginatedList<Invoices>> => {
  const url = adminInvoicesUrls.getInvoicesUrl(filter);

  return authNetworkService
    .get(url, {
      timeout: 50000,
    })
    .then((apiResponse: AxiosResponse<PaginatedList<Invoices>>) => {
      return apiResponse.data;
    });
};

export const getInvoiceCorrespondenceReport = (
  filterProps?: ReportFilterProps,
): Promise<AxiosResponse<Blob>> => {
  const url = adminInvoicesUrls.invoiceCorrespondenceReportUrl();

  return authNetworkService.get(url, {
    responseType: 'blob',
    timeout: 0,
    headers: { Accept: 'application/csv' },
    params: {
      fromDate: filterProps?.fromDate,
      toDate: filterProps?.toDate,
    },
    paramsSerializer: (params) => qs.stringify(params, { allowDots: true }),
  });
};

const sendInvoiceCorrespondence = (invoiceId: string, type: CorrespondenceType) => {
  const url = adminInvoicesUrls.invoiceCorrespondenceUrl();
  return authNetworkService.post(url, { invoiceId, type });
};

export const getInvoiceById = (invoiceId: string): Promise<AxiosResponse<Blob>> => {
  const url = adminInvoicesUrls.getInvoiceUrl(invoiceId);

  return authNetworkService.get(url, { responseType: 'blob' });
};

export const getInvoicePdf = (
  invoiceId: string,
  contentType = ContentTypeEnum.pdf,
): Promise<AxiosResponse<Blob>> => {
  const url = adminInvoicesUrls.getInvoiceContentUrl(invoiceId, contentType);

  return authNetworkService.get(url, { responseType: 'blob', timeout: 0 });
};

const createInvoice = (
  userId: string,
  fleetEntityId?: string,
  fineIds?: string[],
): Promise<string> => {
  const url = adminInvoicesUrls.createInvoiceUrl();

  return authNetworkService
    .post(url, { userId, fleetEntityId, fineIds })
    .then((apiResponse: AxiosResponse<ServiceResult<string>>) => apiResponse.data.data)
    .catch((error) => {
      return Promise.reject(error);
    });
};

const getIndividualInvoice = (invoiceId: string) => {
  const url = adminInvoicesUrls.getIndividualInvoiceUrl(invoiceId);
  return authNetworkService
    .get(url)
    .then((apiResponse: AxiosResponse<ServiceResult<InvoiceDetailDto>>) => apiResponse.data.data);
};

const setPoNumber = (id: Guid, poNumber: string) => {
  const url = adminInvoicesUrls.getInvoiceUrl(id);
  const patchDto = {
    poNumber,
  };
  return authNetworkService.patch(url, patchDto);
};

const updateAdditionalInformationKey = (id: Guid, additionalInformationKey: string) => {
  const url = adminInvoicesUrls.getInvoiceUrl(id);
  const patchDto = {
    additionalInformationKey,
  };
  return authNetworkService.patch(url, patchDto);
};

const updateAdditionalInformationValue = (id: Guid, additionalInformationValue: string) => {
  const url = adminInvoicesUrls.getInvoiceUrl(id);
  const patchDto = {
    additionalInformationValue,
  };
  return authNetworkService.patch(url, patchDto);
};

export default {
  getInvoices,
  createInvoice,
  sendInvoiceCorrespondence,
  getInvoiceCorrespondenceReport,
  getInvoicePdf,
  getInvoiceById,
  getIndividualInvoice,
  setPoNumber,
  updateAdditionalInformationKey,
  updateAdditionalInformationValue,
};
